/**
 * OSTSolutionPicker
 * 
 * Allows users to browse Opportunity Solution Trees and import validated
 * solutions as candidates into a DEGAP cycle.
 * 
 * Integration with Gather phase:
 * - Solutions become candidates in the cycle
 * - Strategic context (opportunity, outcome) is preserved
 * - Two-way traceability: candidates link back to OST nodes
 */

import { useState, useEffect, useMemo, useCallback, type JSX } from 'react';
import {
  TreeDeciduous,
  Target,
  Lightbulb,
  Beaker,
  CheckCircle2,
  Circle,
  ChevronDown,
  ChevronRight,
  Plus,
  ExternalLink,
  AlertCircle,
  Search,
  Filter,
  X,
} from 'lucide-react';
import { useOSTStore, type OSTNode, type OSTTree, buildCandidateFromSolution } from '../../../stores/prioritization/ostStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useDegapProcessStore } from '../../../stores/prioritization/degapProcessStore';
import { getSafeSupabaseClient, generateUUID, getCurrentWorkspaceId } from '../../../lib/supabaseSync';

// ============================================================================
// Types
// ============================================================================

interface OSTSolutionPickerProps {
  cycleId: string;
  workspaceId?: string;
  userId: string;
  onSolutionAdded: (candidateId: string, solutionNode: OSTNode) => void;
  onClose: () => void;
}

type FilterOption = 'all' | 'validated' | 'in_progress' | 'targeted';

// ============================================================================
// Node Type Icons
// ============================================================================

function getNodeIcon(nodeType: string) {
  switch (nodeType) {
    case 'outcome':
      return <Target className="h-4 w-4 text-[var(--text-ter)]" />;
    case 'opportunity':
      return <Lightbulb className="h-4 w-4 text-[var(--mustard)]" />;
    case 'solution':
      return <CheckCircle2 className="h-4 w-4 text-[var(--text-ter)]" />;
    case 'experiment':
      return <Beaker className="h-4 w-4 text-[var(--green)]" />;
    default:
      return <Circle className="h-4 w-4 text-[var(--text-ter)]" />;
  }
}

function getValidationBadge(status?: string) {
  switch (status) {
    case 'validated':
      return (
        <span className="px-2 py-0.5 bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] text-xs font-medium rounded-full">
          Validated
        </span>
      );
    case 'in_progress':
      return (
        <span className="px-2 py-0.5 bg-[var(--slate-dim)] text-[var(--text-sec)] text-xs font-medium rounded-full">
          In Progress
        </span>
      );
    case 'invalidated':
      return (
        <span className="px-2 py-0.5 bg-[var(--coral-dim)] text-[var(--coral)] text-xs font-medium rounded-full">
          Invalidated
        </span>
      );
    default:
      return (
        <span className="px-2 py-0.5 bg-[var(--hover-tint)] text-[var(--text-sec)] text-xs font-medium rounded-full">
          Not Started
        </span>
      );
  }
}

// ============================================================================
// Tree Item Component
// ============================================================================

function TreeItem({
  tree,
  nodes,
  expandedNodes,
  selectedSolutions,
  onToggleExpand,
  onToggleSolution,
}: {
  tree: OSTTree;
  nodes: OSTNode[];
  expandedNodes: Set<string>;
  selectedSolutions: Set<string>;
  onToggleExpand: (nodeId: string) => void;
  onToggleSolution: (solution: OSTNode) => void;
}) {
  // Build tree structure
  const rootNodes = nodes.filter(n => n.parentId === null);
  const _nodeMap = useMemo(() => new Map(nodes.map(n => [n.id, n])), [nodes]);
  const childrenMap = useMemo(() => {
    const map = new Map<string, OSTNode[]>();
    nodes.forEach(n => {
      if (n.parentId) {
        const children = map.get(n.parentId) || [];
        children.push(n);
        map.set(n.parentId, children);
      }
    });
    return map;
  }, [nodes]);

  const renderNode = (node: OSTNode, depth: number = 0): JSX.Element => {
    const children = childrenMap.get(node.id) || [];
    const hasChildren = children.length > 0;
    const isExpanded = expandedNodes.has(node.id);
    const isSelected = selectedSolutions.has(node.id);
    const isSolution = node.nodeType === 'solution';
    const isTargeted = node.isTargeted;

    return (
      <div key={node.id} className="select-none">
        <div
          className={`flex items-center gap-2 py-1.5 px-2  hover:bg-[var(--hover-tint)] cursor-pointer ${
            isTargeted ? 'bg-[var(--mustard-dim)] border-l-2 border-[var(--mustard)]' : ''
          } ${isSolution && isSelected ? 'bg-[var(--slate-dim)] border border-[var(--slate)]' : ''}`}
          style={{ paddingLeft: `${depth * 16 + 8}px` }}
          onClick={() => {
            if (isSolution) {
              onToggleSolution(node);
            } else if (hasChildren) {
              onToggleExpand(node.id);
            }
          }}
        >
          {/* Expand/collapse toggle */}
          {hasChildren ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onToggleExpand(node.id);
              }}
              className="p-0.5 hover:bg-[var(--hover-tint)] rounded"
            >
              {isExpanded ? (
                <ChevronDown className="h-3.5 w-3.5 text-[var(--text-ter)]" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 text-[var(--text-ter)]" />
              )}
            </button>
          ) : (
            <span className="w-5" />
          )}

          {/* Selection checkbox for solutions */}
          {isSolution && (
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleSolution(node)}
              onClick={(e) => e.stopPropagation()}
              className="h-4 w-4 rounded border-[var(--rule)] text-[var(--text-ter)] focus:ring-blue-500"
            />
          )}

          {/* Node icon */}
          {getNodeIcon(node.nodeType)}

          {/* Node title */}
          <span className={`text-sm flex-1 ${isSolution ? 'font-medium' : ''}`}>
            {node.title}
          </span>

          {/* Status badges */}
          {isTargeted && (
            <span className="px-1.5 py-0.5 bg-[var(--mustard-dim)] text-[var(--mustard)] text-xs font-medium rounded">
              Target
            </span>
          )}
          {isSolution && getValidationBadge(node.validationStatus)}
          {node.confidence !== undefined && (
            <span className="text-xs text-[var(--text-ter)]">{node.confidence}%</span>
          )}
        </div>

        {/* Children */}
        {isExpanded && hasChildren && (
          <div>
            {children
              .sort((a, b) => a.sortOrder - b.sortOrder)
              .map(child => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="border border-[var(--rule)] overflow-hidden">
      {/* Tree header */}
      <div className="bg-[var(--surface)] px-4 py-3 border-b border-[var(--rule)]">
        <div className="flex items-center gap-2">
          <TreeDeciduous className="h-5 w-5 text-[var(--green)]" />
          <span className="font-medium text-[var(--text-pri)]">{tree.name}</span>
          {tree.productArea && (
            <span className="text-sm text-[var(--text-ter)]">· {tree.productArea}</span>
          )}
        </div>
        {tree.description && (
          <p className="text-sm text-[var(--text-sec)] mt-1">{tree.description}</p>
        )}
      </div>

      {/* Tree nodes */}
      <div className="p-2 max-h-80 overflow-y-auto">
        {rootNodes.length === 0 ? (
          <p className="text-sm text-[var(--text-ter)] text-center py-4">
            No nodes in this tree
          </p>
        ) : (
          rootNodes.map(node => renderNode(node))
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function OSTSolutionPicker({
  cycleId,
  workspaceId,
  userId,
  onSolutionAdded,
  onClose,
}: OSTSolutionPickerProps) {
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [filter, setFilter] = useState<FilterOption>('all');
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());
  const [selectedSolutions, setSelectedSolutions] = useState<Set<string>>(new Set());
  const [isImporting, setIsImporting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);

  // Stores
  const trees = useOSTStore(state => state.trees);
  const nodes = useOSTStore(state => state.nodes);
  const fetchTrees = useOSTStore(state => state.fetchTrees);
  const fetchNodesWithContext = useOSTStore(state => state.fetchNodesWithContext);
  const isLoading = useOSTStore(state => state.isLoading);

  const addCandidate = useItemsStore(state => state.addProto);
  const addItemsToCycle = useDegapProcessStore(state => state.addItemsToCycle);

  // Fetch trees on mount
  useEffect(() => {
    fetchTrees(workspaceId);
  }, [workspaceId, fetchTrees]);

  // Fetch nodes for each tree
  useEffect(() => {
    trees.forEach(tree => {
      if (!nodes.some(n => n.treeId === tree.id)) {
        fetchNodesWithContext(tree.id);
      }
    });
  }, [trees, nodes, fetchNodesWithContext]);

  // Filter solutions based on search and filter
  const filteredTrees = useMemo(() => {
    return trees.filter(tree => {
      const treeNodes = nodes.filter(n => n.treeId === tree.id);
      const solutions = treeNodes.filter(n => n.nodeType === 'solution');

      // Apply filter
      let filtered = solutions;
      if (filter === 'validated') {
        filtered = solutions.filter(s => s.validationStatus === 'validated');
      } else if (filter === 'in_progress') {
        filtered = solutions.filter(s => s.validationStatus === 'in_progress');
      } else if (filter === 'targeted') {
        const targetedOpps = treeNodes.filter(n => n.nodeType === 'opportunity' && n.isTargeted);
        const targetedOppIds = new Set(targetedOpps.map(o => o.id));
        filtered = solutions.filter(s => targetedOppIds.has(s.parentId || ''));
      }

      // Apply search
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(s =>
          s.title.toLowerCase().includes(query) ||
          s.description?.toLowerCase().includes(query) ||
          s.parentOpportunityTitle?.toLowerCase().includes(query)
        );
      }

      return filtered.length > 0;
    });
  }, [trees, nodes, filter, searchQuery]);

  // Toggle node expansion
  const handleToggleExpand = useCallback((nodeId: string) => {
    setExpandedNodes(prev => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  // Toggle solution selection
  const handleToggleSolution = useCallback((solution: OSTNode) => {
    setSelectedSolutions(prev => {
      const next = new Set(prev);
      if (next.has(solution.id)) {
        next.delete(solution.id);
      } else {
        next.add(solution.id);
      }
      return next;
    });
  }, []);

  // Import selected solutions as candidates
  const handleImport = useCallback(async () => {
    if (selectedSolutions.size === 0) return;

    setIsImporting(true);
    setImportError(null);

    try {
      const itemIds: string[] = [];
      const importedSolutions: Array<{ solutionId: string; opportunityId: string; treeId: string; candidateId: string }> = [];

      for (const solutionId of selectedSolutions) {
        const solution = nodes.find(n => n.id === solutionId);
        if (!solution) continue;

        // Find parent opportunity
        const opportunity = nodes.find(n => n.id === solution.parentId);
        if (!opportunity) {
          console.warn(`[OSTSolutionPicker] Could not find parent opportunity for solution ${solutionId}`);
          continue;
        }

        // Find outcome (root of tree)
        const outcome = nodes.find(n => n.treeId === solution.treeId && n.nodeType === 'outcome' && n.parentId === null);
        if (!outcome) {
          console.warn(`[OSTSolutionPicker] Could not find outcome for tree ${solution.treeId}`);
          continue;
        }

        // Build candidate data
        const candidateData = buildCandidateFromSolution({
          solution,
          opportunity,
          outcome,
          cycleId,
          createdBy: userId,
        });

        // Create candidate (addProto returns void, so we need to generate ID)
        const candidateId = crypto.randomUUID();
        addCandidate({
          id: candidateId,
          title: candidateData.title,
          description: candidateData.description,
          source: 'ost',
          candidateType: 'idea',
          createdBy: userId,
          tags: ['from-ost'],
          metadata: candidateData.metadata,
          ostNodeId: solution.id,
          ostOpportunityId: opportunity.id,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- legacy addProto payload; typed in the store burndown
        } as any);

        itemIds.push(candidateId);
        importedSolutions.push({
          solutionId: solution.id,
          opportunityId: opportunity.id,
          treeId: solution.treeId,
          candidateId,
        });
        onSolutionAdded(candidateId, solution);
      }

      // Add candidates to cycle
      if (itemIds.length > 0) {
        await addItemsToCycle(cycleId, itemIds);
        console.log(`[OSTSolutionPicker] Imported ${itemIds.length} solutions as items`);
        
        // =========================================================================
        // GOVERNANCE: Emit audit event for OST imports (per Doc 05)
        // 
        // Per Build Contract section 2.5: For any value that can affect ranking,
        // selection, inclusion or exclusion - the system must persist who, when, why.
        // OST imports affect Item Pool composition and must be auditable.
        // =========================================================================
        const supabase = getSafeSupabaseClient();
        if (supabase) {
          try {
            await supabase.from('governance_events').insert({
              id: generateUUID(),
              cycle_id: cycleId,
              workspace_id: getCurrentWorkspaceId() ?? null,
              event_type: 'candidates_imported_from_ost',
              actor_id: userId,
              occurred_at: new Date().toISOString(),
              note: JSON.stringify({
                source: 'opportunity_solution_tree',
                itemCount: itemIds.length,
                itemIds,
                solutions: importedSolutions,
              }),
            });
            console.log(`[OSTSolutionPicker] Governance event recorded: candidates_imported_from_ost`);
          } catch (govErr) {
            // Governance events are best-effort; log but don't block the import
            console.warn('[OSTSolutionPicker] Governance event failed (non-blocking):', govErr);
          }
        }
      }

      // Clear selection and close
      setSelectedSolutions(new Set());
      onClose();
    } catch (err) {
      console.error('[OSTSolutionPicker] Import error:', err);
      setImportError(err instanceof Error ? err.message : 'Failed to import solutions');
    } finally {
      setIsImporting(false);
    }
  }, [selectedSolutions, nodes, cycleId, userId, addCandidate, addItemsToCycle, onSolutionAdded, onClose]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-[var(--bg-raised)] shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-[var(--rule)] flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-[var(--text-pri)] flex items-center gap-2">
              <TreeDeciduous className="h-5 w-5 text-[var(--green)]" />
              Import from Opportunity Trees
            </h2>
            <p className="text-sm text-[var(--text-ter)] mt-1">
              Select validated solutions to add as candidates to this cycle
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[var(--hover-tint)]"
          >
            <X className="h-5 w-5 text-[var(--text-ter)]" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-3 border-b border-[var(--rule)] flex items-center gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[var(--text-ter)]" />
            <input
              type="text"
              placeholder="Search solutions..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 border border-[var(--rule)] text-sm focus:ring-2 focus:ring-blue-500 focus:border-[var(--slate)]"
            />
          </div>

          {/* Filter */}
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-[var(--text-ter)]" />
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value as FilterOption)}
              className="bg-[var(--bg-raised)] text-[var(--text-pri)] border border-[var(--rule)] px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Solutions</option>
              <option value="validated">Validated Only</option>
              <option value="in_progress">In Progress</option>
              <option value="targeted">From Targeted Opportunities</option>
            </select>
          </div>
        </div>

        {/* Tree list */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {isLoading ? (
            <div className="text-center py-8">
              <div className="animate-spin h-8 w-8 border-2 border-[var(--slate)] border-t-transparent rounded-full mx-auto" />
              <p className="text-sm text-[var(--text-ter)] mt-2">Loading opportunity trees...</p>
            </div>
          ) : filteredTrees.length === 0 ? (
            <div className="text-center py-8">
              <TreeDeciduous className="h-12 w-12 text-[var(--text-ter)] mx-auto" />
              <p className="text-[var(--text-ter)] mt-2">
                {trees.length === 0
                  ? 'No opportunity trees found'
                  : 'No solutions match your filter'}
              </p>
              {trees.length === 0 && (
                <a
                  href="/prioritization/strategy/opportunity-tree"
                  className="inline-flex items-center gap-1 text-[var(--text-ter)] hover:underline mt-2"
                >
                  Create an Opportunity Tree
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
          ) : (
            filteredTrees.map(tree => (
              <TreeItem
                key={tree.id}
                tree={tree}
                nodes={nodes.filter(n => n.treeId === tree.id)}
                expandedNodes={expandedNodes}
                selectedSolutions={selectedSolutions}
                onToggleExpand={handleToggleExpand}
                onToggleSolution={handleToggleSolution}
              />
            ))
          )}
        </div>

        {/* Error */}
        {importError && (
          <div className="px-6 py-3 bg-[var(--coral-dim)] border-t border-[var(--coral)] flex items-center gap-2 text-[var(--coral)]">
            <AlertCircle className="h-4 w-4" />
            <span className="text-sm">{importError}</span>
          </div>
        )}

        {/* Footer */}
        <div className="px-6 py-4 border-t border-[var(--rule)] flex items-center justify-between">
          <div className="text-sm text-[var(--text-sec)]">
            {selectedSolutions.size > 0
              ? `${selectedSolutions.size} solution${selectedSolutions.size > 1 ? 's' : ''} selected`
              : 'Select solutions to import'}
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={selectedSolutions.size === 0 || isImporting}
              className="btn btn-primary flex items-center gap-2"
            >
              {isImporting ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  Importing...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Import to Cycle
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default OSTSolutionPicker;
